import { d as addItemByForm, a as contexts } from './cart-e7eff8c7.js';
import { E as EVENTS } from './events-80a90688.js';

class ProductBlockBuyButtons extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      productContainer: "main-product-default",
      detailsContainer: ".product__details",
      storeAvailabilityDrawerTrigger: "[js-store-availability-drawer-trigger]",
      freeShippingBar: "[js-free-shipping-bar]",

      // Add to cart success and error
      addToCartButton: "[js-add-to-cart-button-wrapper]",
      addToCartError: "[js-add-to-cart-error]",
      giftCardRecipientForm: ".gift-card-recipient__fields",
      giftCardRecipientInput: ".gift-card-recipient__input",
      productForm: "[data-product-form]",
      showGiftCardRecipientCheckbox: "#gift-card-show-form",
    };

    this.classes = {
      hasError: "has-error",
      disabled: "disabled",
      hidden: "hidden",
      changePending: "change-pending",
    };

    this.strings = window.theme.strings;
  }

  connectedCallback() {
    this.controller = new AbortController();
    this.productContainer = document.querySelector(
      this.selectors.productContainer,
    );
    this.detailsContainer = this.productContainer.querySelector(
      this.selectors.detailsContainer,
    );
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);
    this.sectionId = this.dataset.sectionId;

    // Listen for quantity change
    this.productContainer.addEventListener(
      EVENTS.QUANTITY_INPUT_CHANGE,
      () => {
        this.classList.remove(this.classes.hasError);
      },
      { signal: this.controller.signal },
    );

    // Listen for variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint.bind(this),
      { signal: this.controller.signal },
    );

    // Keep the free shipping progress bar in sync with the cart total.
    // The bar is server-rendered from `cart.total_price`, so without this
    // it would only reflect reality after a full page reload.
    document.addEventListener(
      EVENTS.CART_CHANGED,
      this.refreshFreeShippingBar.bind(this),
      { signal: this.controller.signal },
    );

    this.initContentListeners();
  }

  //
  // General setup functions
  //
  initContentListeners() {
    this.contentListenersController = new AbortController();

    // Check gift card recipient form setting and gift card product
    this.enableGiftCardRecipientForm =
      this.getAttribute("data-enable-gift-card-recipient-form") === "true";

    this.showGiftCardRecipientCheckbox = this.querySelector(
      this.selectors.showGiftCardRecipientCheckbox,
    );
    if (
      this.enableGiftCardRecipientForm &&
      this.showGiftCardRecipientCheckbox
    ) {
      this.setupGiftCardRecipientForm();
    }

    // Listen for Store availability drawer click
    this.storeAvailabilityDrawerTrigger = this.querySelector(
      this.selectors.storeAvailabilityDrawerTrigger,
    );

    if (this.storeAvailabilityDrawerTrigger) {
      this.drawerId =
        this.storeAvailabilityDrawerTrigger.getAttribute("data-drawer-id");

      this.storeAvailabilityDrawerTrigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent(
              EVENTS.MODAL_STORE_AVAILABILITY_DRAWER_OPEN(this.drawerId),
            ),
          );
        },
        { signal: this.contentListenersController.signal },
      );
    }

    // Listen for Add to cart submit
    this.productForm = this.querySelector(this.selectors.productForm);
    this.productForm.addEventListener(
      "submit",
      (event) => {
        this.handleFormSubmission(event);
      },
      { signal: this.contentListenersController.signal },
    );

    // Listen for Add to cart error at product container level
    this.addToCartError = this.querySelector(this.selectors.addToCartError);
    this.addEventListener(
      EVENTS.PRODUCT_ADD_ERROR,
      this.handleAddToCartError.bind(this),
      { signal: this.contentListenersController.signal },
    );

    // Listen for successfull add to cart
    this.addEventListener(
      EVENTS.PRODUCT_ADD_SUCCESS,
      () => {
        this.classList.remove(this.classes.hasError);
        this.resetGiftCardRecipientForm();
        this.handleAddToCartSuccess();
      },
      { signal: this.contentListenersController.signal },
    );
  }

  setupGiftCardRecipientForm() {
    this.giftCardRecipientForm = this.querySelector(
      this.selectors.giftCardRecipientForm,
    );
    this.giftCardRecipientInputs = this.querySelectorAll(
      this.selectors.giftCardRecipientInput,
    );

    // Set default form visibility
    this.showGiftCardRecipientCheckbox.checked = false;
    this.giftCardRecipientForm.style.display = "none";

    // Listen for checked 'show form' input
    this.showGiftCardRecipientCheckbox.addEventListener(
      "change",
      () => {
        if (this.showGiftCardRecipientCheckbox.checked) {
          this.giftCardRecipientForm.style.display = "block";
        } else {
          // reset required to 'disable' the form's requirements
          this.resetGiftCardRecipientForm();
        }
      },
      { signal: this.controller.signal },
    );
  }

  resetGiftCardRecipientForm() {
    if (!this.giftCardRecipientInputs) {
      return
    }

    this.giftCardRecipientInputs.forEach((input) => {
      input.value = "";
      input.classList.remove("has-error");
    });

    this.showGiftCardRecipientCheckbox.checked = false;
    this.giftCardRecipientForm.style.display = "none";
  }

  //
  // Variant and quantity change handling functions
  //
  handleRepaint() {
    this.contentListenersController.abort();
    this.addToCartButton = this.querySelector(this.selectors.addToCartButton);
    this.initContentListeners();
  }

  //
  // Free shipping bar refresh
  //
  refreshFreeShippingBar() {
    if (!this.sectionId) {
      return
    }

    const freeShippingBar = this.querySelector(this.selectors.freeShippingBar);

    if (!freeShippingBar) {
      return
    }

    fetch(`${window.location.pathname}?section_id=${this.sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const newHTML = document
          .createRange()
          .createContextualFragment(responseText);

        const newFreeShippingBar = newHTML.querySelector(
          this.selectors.freeShippingBar,
        );

        const currentFreeShippingBar = this.querySelector(
          this.selectors.freeShippingBar,
        );

        if (!currentFreeShippingBar) {
          return
        }

        if (!newFreeShippingBar) {
          // Threshold settings may have changed or the bar was disabled;
          // simplest safe outcome is to remove the now-stale bar.
          currentFreeShippingBar.remove();
          return
        }

        // We persist the old style as we swap the content and then update with
        // the new style once swapped, so that the progress bar transitions
        // between values instead of jumping instantly (mirrors the cart
        // drawer's free shipping bar update behavior).
        const oldStyle = currentFreeShippingBar.getAttribute("style");
        const newStyle = newFreeShippingBar.getAttribute("style");

        newFreeShippingBar.setAttribute("style", oldStyle);
        currentFreeShippingBar.replaceWith(newFreeShippingBar);

        setTimeout(() => {
          this.querySelector(this.selectors.freeShippingBar)?.setAttribute(
            "style",
            newStyle,
          );
        }, 100);
      })
      .catch(() => {
        // Fail silently: worst case the bar stays as-is until reload.
      });
  }

  //
  // Add to cart and results handling functions
  //
  handleFormSubmission(event) {
    event.preventDefault();

    this.addToCartButton.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_STARTED),
    );

    this.detailsContainer.classList.add(this.classes.changePending);
    addItemByForm(this.productForm, contexts.productPage, this);
  }

  handleAddToCartSuccess() {
    this.detailsContainer.classList.remove(this.classes.changePending);
    this.addToCartButton.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_COMPLETED),
    );
  }

  handleAddToCartError(event) {
    this.detailsContainer.classList.remove(this.classes.changePending);
    this.addToCartButton.dispatchEvent(
      new CustomEvent(EVENTS.BUTTON_STATE_INCOMPLETED),
    );

    const formErrors = event.detail.errorMessage.errors;
    this.classList.add(this.classes.hasError);

    // Gift card recipient errors can come with better error context
    if (formErrors) {
      const sectionId = this.querySelector(
        this.selectors.giftCardRecipientForm,
      ).getAttribute("data-section-id");
      this.addToCartError.innerText = "";

      Object.entries(formErrors).forEach(([key]) => {
        // Update error field visual and aria
        const errorMessageID = `gift-card-recipient-${key}-error--${sectionId}`;
        const errorInput = this.querySelector(
          `#gift-card-recipient-${key}--${sectionId}`,
        );
        errorInput.classList.add(this.classes.hasError);
        errorInput.setAttribute("aria-invalid", true);
        errorInput.setAttribute("aria-describedby", errorMessageID);

        // List errors together above add to cart button
        this.addToCartError.innerText +=
          `${this.strings.products.giftCardRecipientErrors[key]}` + "\n";
      });
    } else {
      this.addToCartError.innerText = event.detail.errorMessage.description;
    }
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-blocks-buy-buttons")) {
  customElements.define("product-block-buy-buttons", ProductBlockBuyButtons);
}
